// src/pages/FleetSentry.jsx
// Fleet Sentry - AI-Powered Fleet Management Demo
// Edge AI Platform for Roadside Assistance & Fleet Operations
// MWC Barcelona Demo - Celerway Stratus

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK FLEET DATA - Simulates RAC-style roadside assistance fleet
// ════════════════════════════════════════════════════════════════════════════

const MOCK_FLEET_GATEWAY = {
  id: 'gateway-london-south',
  name: 'London South Fleet Hub',
  location: 'Croydon Service Center',
  status: 'Online',
  vehicleCount: 8,
  activeCallouts: 3,
  stratusModel: 'Stratus 5G Pro',
  connectivity: '5G',
  lastSync: new Date().toISOString(),
};

const MOCK_VEHICLES = [
  {
    id: 'RAC-4521',
    vin: 'WBA3A5C51CF256721',
    type: 'Service Van',
    driver: 'James Mitchell',
    status: 'en-route',
    location: { lat: 51.3762, lng: -0.0982, address: 'A23 Purley Way' },
    battery: 12.1,  // Low - trigger alert
    fuel: 67,
    engineTemp: 92,
    odometer: 45892,
    lastDiagnostic: '2025-01-31T14:30:00Z',
    diagnosticCodes: ['P0171'],  // Fuel system lean
    connectivity: '5G',
    currentJob: 'Flat tyre - M25 J7',
    eta: '12 mins',
  },
  {
    id: 'RAC-4522',
    vin: 'WBA3A5C51CF256722',
    type: 'Service Van',
    driver: 'Sarah Collins',
    status: 'on-job',
    location: { lat: 51.4015, lng: -0.1248, address: 'Streatham High Rd' },
    battery: 14.2,
    fuel: 45,
    engineTemp: 88,
    odometer: 62341,
    lastDiagnostic: '2025-01-31T14:28:00Z',
    diagnosticCodes: [],
    connectivity: '5G',
    currentJob: 'Battery replacement - Streatham',
    eta: null,
  },
  {
    id: 'RAC-4523',
    vin: 'WBA3A5C51CF256723',
    type: 'Heavy Recovery',
    driver: 'Mike Thompson',
    status: 'available',
    location: { lat: 51.3557, lng: -0.1682, address: 'Croydon Depot' },
    battery: 14.4,
    fuel: 89,
    engineTemp: 45,  // Cold - parked
    odometer: 89234,
    lastDiagnostic: '2025-01-31T14:25:00Z',
    diagnosticCodes: [],
    connectivity: '5G',
    currentJob: null,
    eta: null,
  },
  {
    id: 'RAC-4524',
    vin: 'WBA3A5C51CF256724',
    type: 'Service Van',
    driver: 'Emma Williams',
    status: 'en-route',
    location: { lat: 51.4194, lng: -0.0876, address: 'Brixton Road' },
    battery: 14.1,
    fuel: 23,  // Low fuel - trigger alert
    engineTemp: 91,
    odometer: 51203,
    lastDiagnostic: '2025-01-31T14:32:00Z',
    diagnosticCodes: [],
    connectivity: '4G',
    currentJob: 'Lockout - Brixton',
    eta: '8 mins',
  },
  {
    id: 'RAC-4525',
    vin: 'WBA3A5C51CF256725',
    type: 'Service Van',
    driver: 'David Brown',
    status: 'offline',  // Lost connectivity
    location: { lat: 51.3891, lng: -0.0543, address: 'Last: Penge High St' },
    battery: 13.8,
    fuel: 56,
    engineTemp: 89,
    odometer: 38921,
    lastDiagnostic: '2025-01-31T13:45:00Z',  // Old - connectivity issue
    diagnosticCodes: ['U0100'],  // Lost communication with ECM
    connectivity: 'Lost',
    currentJob: 'Unknown - Last: Jump start',
    eta: null,
  },
  {
    id: 'RAC-4526',
    vin: 'WBA3A5C51CF256726',
    type: 'Motorcycle',
    driver: 'Tom Anderson',
    status: 'available',
    location: { lat: 51.3621, lng: -0.1912, address: 'Croydon Depot' },
    battery: 12.8,
    fuel: 78,
    engineTemp: 42,
    odometer: 12453,
    lastDiagnostic: '2025-01-31T14:20:00Z',
    diagnosticCodes: [],
    connectivity: '5G',
    currentJob: null,
    eta: null,
  },
  {
    id: 'RAC-4527',
    vin: 'WBA3A5C51CF256727',
    type: 'Service Van',
    driver: 'Rachel Green',
    status: 'on-job',
    location: { lat: 51.4312, lng: -0.1547, address: 'Clapham Common' },
    battery: 14.3,
    fuel: 71,
    engineTemp: 94,
    odometer: 67823,
    lastDiagnostic: '2025-01-31T14:31:00Z',
    diagnosticCodes: [],
    connectivity: '5G',
    currentJob: 'Tyre change - Clapham',
    eta: null,
  },
  {
    id: 'RAC-4528',
    vin: 'WBA3A5C51CF256728',
    type: 'Heavy Recovery',
    driver: 'Chris Taylor',
    status: 'en-route',
    location: { lat: 51.3478, lng: -0.0234, address: 'A21 Bromley' },
    battery: 14.0,
    fuel: 82,
    engineTemp: 102,  // High temp - trigger alert
    odometer: 94521,
    lastDiagnostic: '2025-01-31T14:33:00Z',
    diagnosticCodes: ['P0217'],  // Engine overtemp
    connectivity: '5G',
    currentJob: 'Vehicle recovery - A21',
    eta: '22 mins',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES - Fleet-specific
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🚨', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  ATTENTION: { icon: '⚠️', text: 'ATTENTION', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  MONITORING: { icon: '📡', text: 'MONITORING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', color: 'text-purple-400', pulse: true },
  OPERATIONAL: { icon: '✓', text: 'OPERATIONAL', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  OFFLINE: { icon: '○', text: 'OFFLINE', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const VEHICLE_STATUS_COLORS = {
  'en-route': { bg: 'bg-blue-500', text: 'text-blue-300', label: 'En Route' },
  'on-job': { bg: 'bg-emerald-500', text: 'text-emerald-300', label: 'On Job' },
  'available': { bg: 'bg-cyan-500', text: 'text-cyan-300', label: 'Available' },
  'offline': { bg: 'bg-red-500', text: 'text-red-300', label: 'Offline' },
  'maintenance': { bg: 'bg-amber-500', text: 'text-amber-300', label: 'Maintenance' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Analyzes fleet data for issues
// ════════════════════════════════════════════════════════════════════════════

const detectFleetAnomalies = (vehicles) => {
  const anomalies = [];
  
  vehicles.forEach(vehicle => {
    // Low battery detection
    if (vehicle.battery < 12.4) {
      anomalies.push({
        id: `battery-${vehicle.id}`,
        type: 'BATTERY_LOW',
        vehicleId: vehicle.id,
        severity: vehicle.battery < 12.0 ? 'critical' : 'warning',
        metric: `${vehicle.battery}V`,
        threshold: '12.4V',
        description: `Battery voltage critically low`,
        recommendation: vehicle.status === 'en-route' 
          ? 'Complete current job then return to depot for battery check'
          : 'Schedule immediate battery inspection',
        confidence: 94,
      });
    }
    
    // Low fuel detection
    if (vehicle.fuel < 25) {
      anomalies.push({
        id: `fuel-${vehicle.id}`,
        type: 'FUEL_LOW',
        vehicleId: vehicle.id,
        severity: vehicle.fuel < 15 ? 'critical' : 'warning',
        metric: `${vehicle.fuel}%`,
        threshold: '25%',
        description: `Fuel level low`,
        recommendation: 'Route to nearest fuel station before next callout',
        confidence: 98,
      });
    }
    
    // High engine temp
    if (vehicle.engineTemp > 100) {
      anomalies.push({
        id: `temp-${vehicle.id}`,
        type: 'ENGINE_OVERTEMP',
        vehicleId: vehicle.id,
        severity: 'critical',
        metric: `${vehicle.engineTemp}°C`,
        threshold: '100°C',
        description: `Engine temperature exceeds safe limit`,
        recommendation: 'Pull over safely and allow engine to cool. Do not continue driving.',
        confidence: 97,
      });
    }
    
    // Connectivity loss
    if (vehicle.connectivity === 'Lost' || vehicle.status === 'offline') {
      anomalies.push({
        id: `connectivity-${vehicle.id}`,
        type: 'CONNECTIVITY_LOST',
        vehicleId: vehicle.id,
        severity: 'critical',
        metric: 'No Signal',
        threshold: 'Connected',
        description: `Vehicle offline - last contact ${getTimeAgo(vehicle.lastDiagnostic)}`,
        recommendation: 'Attempt phone contact with driver. Dispatch nearest unit to last known location if unreachable.',
        confidence: 89,
      });
    }
    
    // Diagnostic trouble codes
    if (vehicle.diagnosticCodes && vehicle.diagnosticCodes.length > 0) {
      vehicle.diagnosticCodes.forEach(code => {
        if (code !== 'U0100') { // Already handled in connectivity
          anomalies.push({
            id: `dtc-${vehicle.id}-${code}`,
            type: 'DIAGNOSTIC_CODE',
            vehicleId: vehicle.id,
            severity: code.startsWith('P0') ? 'warning' : 'info',
            metric: code,
            threshold: 'No codes',
            description: getDTCDescription(code),
            recommendation: 'Schedule maintenance inspection',
            confidence: 85,
          });
        }
      });
    }
  });
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getDTCDescription = (code) => {
  const codes = {
    'P0171': 'Fuel system too lean (Bank 1)',
    'P0217': 'Engine coolant over temperature',
    'P0300': 'Random/multiple cylinder misfire',
    'U0100': 'Lost communication with ECM/PCM',
  };
  return codes[code] || `Diagnostic code: ${code}`;
};

const getTimeAgo = (isoString) => {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
};

const getVehicleAIStatus = (vehicle, anomalies) => {
  const vehicleAnomalies = anomalies.filter(a => a.vehicleId === vehicle.id);
  
  if (vehicle.status === 'offline') return AI_STATUS.OFFLINE;
  
  const hasCritical = vehicleAnomalies.some(a => a.severity === 'critical');
  const hasWarning = vehicleAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical) return AI_STATUS.CRITICAL;
  if (hasWarning) return AI_STATUS.ATTENTION;
  if (vehicle.status === 'en-route') return AI_STATUS.MONITORING;
  return AI_STATUS.OPERATIONAL;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Cloud/Fleet Command Node
const FleetCommandNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400/30 via-blue-400/30 to-purple-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-cyan-400/50 rounded-2xl p-6 min-w-[220px] shadow-2xl shadow-cyan-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-400/30 to-blue-500/30 rounded-xl flex items-center justify-center border border-cyan-400/50">
          <svg className="w-8 h-8 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 flex justify-center gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.gatewayCount || 1}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Hubs</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.vehicleCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Vehicles</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">{data.activeCallouts || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Active</div>
        </div>
      </div>
      
      {/* AI Sentry Active indicator */}
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        <span className="text-[10px] text-cyan-300 font-medium">AI Sentry Active</span>
      </div>
    </div>
  </div>
);

// Fleet Gateway Node (Stratus at depot/hub)
const FleetGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[240px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 flex justify-between text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Vehicles</span>
            <div className="font-semibold text-blue-300">{data.vehicleCount}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Active</span>
            <div className="font-semibold text-amber-300">{data.activeCallouts}</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Vehicle Node
const VehicleNode = ({ data }) => {
  const statusInfo = VEHICLE_STATUS_COLORS[data.status] || VEHICLE_STATUS_COLORS['available'];
  const aiStatus = data.aiStatus || AI_STATUS.OPERATIONAL;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.ATTENTION;
  
  const typeIcons = {
    'Service Van': '🚐',
    'Heavy Recovery': '🚛',
    'Motorcycle': '🏍️',
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {/* Alert glow */}
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : 'border-slate-500/50'} rounded-lg p-3 min-w-[180px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Vehicle header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="text-2xl">{typeIcons[data.type] || '🚗'}</div>
          <div>
            <div className="text-sm font-bold text-white">{data.id}</div>
            <div className="text-[10px] text-slate-400">{data.type}</div>
          </div>
        </div>
        
        {/* Driver */}
        <div className="text-xs text-slate-300 mb-2">
          <span className="text-slate-500">Driver:</span> {data.driver}
        </div>
        
        {/* Status badge */}
        <div className="mb-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.bg}/30 ${statusInfo.text} border border-current/30`}>
            {statusInfo.label}
          </span>
        </div>
        
        {/* Current job if active */}
        {data.currentJob && (
          <div className="bg-slate-800/60 rounded px-2 py-1.5 mb-2">
            <div className="text-[10px] text-slate-500 uppercase">Current Job</div>
            <div className="text-xs text-cyan-300 truncate">{data.currentJob}</div>
            {data.eta && <div className="text-[10px] text-emerald-400">ETA: {data.eta}</div>}
          </div>
        )}
        
        {/* Key metrics */}
        <div className="grid grid-cols-3 gap-1 text-center">
          <div className={`rounded px-1 py-0.5 ${data.battery < 12.4 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
            <div className={`text-[10px] font-bold ${data.battery < 12.4 ? 'text-red-300' : 'text-slate-300'}`}>{data.battery}V</div>
            <div className="text-[8px] text-slate-500">BAT</div>
          </div>
          <div className={`rounded px-1 py-0.5 ${data.fuel < 25 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
            <div className={`text-[10px] font-bold ${data.fuel < 25 ? 'text-amber-300' : 'text-slate-300'}`}>{data.fuel}%</div>
            <div className="text-[8px] text-slate-500">FUEL</div>
          </div>
          <div className={`rounded px-1 py-0.5 ${data.engineTemp > 100 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
            <div className={`text-[10px] font-bold ${data.engineTemp > 100 ? 'text-red-300' : 'text-slate-300'}`}>{data.engineTemp}°</div>
            <div className="text-[8px] text-slate-500">TEMP</div>
          </div>
        </div>
        
        {/* Connectivity indicator */}
        <div className="mt-2 flex items-center justify-center gap-1">
          <div className={`w-1.5 h-1.5 rounded-full ${data.connectivity === 'Lost' ? 'bg-red-400' : data.connectivity === '5G' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
          <span className="text-[9px] text-slate-400">{data.connectivity}</span>
        </div>
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  fleetCommand: FleetCommandNode,
  fleetGateway: FleetGatewayNode,
  vehicle: VehicleNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, vehicles, onExecuteAction }) => {
  const [activeTab, setActiveTab] = useState('detections');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
    // In real implementation, would call Nimbus API
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const warningCount = anomalies.filter(a => a.severity === 'warning').length;
  
  return (
    <div className="absolute top-0 right-0 h-full w-96 bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🤖</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI Sentry Fleet Monitor</h3>
              <div className="text-[10px] text-slate-400">{anomalies.length} detections • {vehicles.length} vehicles</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Status bar */}
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Monitoring Active
          </div>
          <div className="text-[10px] text-slate-500">Nimbus Integration Ready</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('detections')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'detections'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🎯 Detections
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('actions')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'actions'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          ⚡ Actions
        </button>
        <button
          onClick={() => setActiveTab('fleet')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'fleet'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          🚐 Fleet
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'detections' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All fleet systems nominal</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const vehicle = vehicles.find(v => v.id === anomaly.vehicleId);
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg ${anomaly.severity === 'critical' ? '' : ''}`}>
                          {anomaly.severity === 'critical' ? '🚨' : '⚠️'}
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-white">{anomaly.vehicleId}</div>
                          <div className="text-[10px] text-slate-400">{vehicle?.driver}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                      <span className="text-[10px] text-slate-500">threshold: {anomaly.threshold}</span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleExecuteAction(anomaly)}
                        disabled={executingAction === anomaly.id}
                        className="flex-1 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1"
                      >
                        {executingAction === anomaly.id ? (
                          <>
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Executing...
                          </>
                        ) : (
                          <>✓ Execute</>
                        )}
                      </button>
                      <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                        Dismiss
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'actions' && (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-lg border border-cyan-500/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🌐</span>
                <div>
                  <div className="text-sm font-semibold text-white">Network Response</div>
                  <div className="text-[10px] text-slate-400">Nimbus API Action</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-bold text-emerald-400">95%</div>
                  <div className="text-[10px] text-slate-500">Confidence</div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Target Gateway</div>
                <div className="text-sm text-cyan-300 font-mono">London South Fleet Hub</div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-amber-400">🏷️</span>
                  <span className="text-sm text-white">Priority Dispatch Tag</span>
                </div>
                <div className="font-mono text-xs text-cyan-300 bg-slate-900/50 px-2 py-1 rounded">
                  aisentry-priority-{Date.now().toString().slice(-8)}
                </div>
                <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-400">
                  <span className="text-emerald-400">✓</span>
                  Visible in Nimbus Portal
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Responding to</div>
                <div className="flex items-center gap-2">
                  <span className="text-red-400 font-mono text-sm">RAC-4525</span>
                  <span className="text-slate-500">•</span>
                  <span className="text-xs text-slate-400">Connectivity Lost</span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  ✓ Approve & Execute
                </button>
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
            
            <div className="text-center text-[10px] text-slate-500 pt-2">
              Powered by <span className="text-cyan-400">AI Sentry</span>
            </div>
          </div>
        )}
        
        {activeTab === 'fleet' && (
          <div className="space-y-2">
            {vehicles.map(vehicle => {
              const statusInfo = VEHICLE_STATUS_COLORS[vehicle.status];
              return (
                <div key={vehicle.id} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{vehicle.type === 'Service Van' ? '🚐' : vehicle.type === 'Heavy Recovery' ? '🚛' : '🏍️'}</span>
                      <div>
                        <div className="text-sm font-semibold text-white">{vehicle.id}</div>
                        <div className="text-[10px] text-slate-400">{vehicle.driver}</div>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.bg}/30 ${statusInfo.text}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  {vehicle.currentJob && (
                    <div className="text-xs text-slate-300 truncate">{vehicle.currentJob}</div>
                  )}
                  <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
                    <span>🔋 {vehicle.battery}V</span>
                    <span>⛽ {vehicle.fuel}%</span>
                    <span>📡 {vehicle.connectivity}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Last scan: Just now</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT CARD
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, vehicles, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  const topAnomaly = criticalAnomalies[0];
  const vehicle = vehicles.find(v => v.id === topAnomaly.vehicleId);
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/90 to-slate-900/90 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl animate-pulse">🚨</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white mb-1">
              Attention Required
            </div>
            <div className="text-xs text-red-200 mb-2">
              {topAnomaly.description}
            </div>
            <div className="text-[10px] text-slate-400 mb-3">
              {topAnomaly.vehicleId} • {vehicle?.driver} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more` : topAnomaly.type.replace(/_/g, ' ')}
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ vehicles, anomalies, onOpenPanel }) => {
  const online = vehicles.filter(v => v.status !== 'offline').length;
  const onJob = vehicles.filter(v => v.status === 'on-job' || v.status === 'en-route').length;
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-emerald-300 font-bold text-lg">{online}</span>
        <span className="text-slate-300 text-xs">/ {vehicles.length} Online</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-blue-300 font-bold text-lg">{onJob}</span>
        <span className="text-slate-300 text-xs">Active</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">Alerts</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg px-4 py-2 border border-cyan-400/50 transition-colors"
      >
        <span className="text-cyan-300 text-sm">🤖</span>
        <span className="text-cyan-300 text-xs font-medium">AI Sentry</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-cyan-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-emerald-400" />
        <span className="text-xs text-slate-300">Online</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <span className="text-xs text-slate-300">Offline / Alert</span>
      </div>
    </div>
    <div className="pt-2 border-t border-slate-600/50 space-y-2">
      <div className="text-[10px] text-slate-400 uppercase">AI Status</div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-red-500" />
        <span className="text-xs text-slate-300">Critical</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-amber-500" />
        <span className="text-xs text-slate-300">Attention</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-purple-500" />
        <span className="text-xs text-slate-300">Monitoring</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-emerald-500" />
        <span className="text-xs text-slate-300">Operational</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const FleetSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [vehicles] = useState(MOCK_VEHICLES);
  const [gateway] = useState(MOCK_FLEET_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build topology
  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectFleetAnomalies(vehicles);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants
    const CLOUD_Y = 0;
    const GATEWAY_Y = 180;
    const VEHICLE_Y_START = 400;
    const VEHICLE_GAP = 200;
    const CENTER_X = 600;
    
    // 1. Fleet Command (Cloud)
    const activeCallouts = vehicles.filter(v => v.status === 'on-job' || v.status === 'en-route').length;
    newNodes.push({
      id: 'fleet-command',
      type: 'fleetCommand',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'Fleet Command',
        sublabel: 'Central Operations',
        gatewayCount: 1,
        vehicleCount: vehicles.length,
        activeCallouts,
      },
    });
    
    // 2. Fleet Gateway (Stratus at depot)
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'fleetGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: {
        ...gateway,
      },
    });
    
    // Edge: Cloud → Gateway
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'fleet-command',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#34d399', strokeWidth: 2 },
    });
    
    // 3. Vehicles
    const vehicleStartX = CENTER_X - ((vehicles.length - 1) * VEHICLE_GAP) / 2;
    
    vehicles.forEach((vehicle, idx) => {
      const aiStatus = getVehicleAIStatus(vehicle, detectedAnomalies);
      const vehicleId = `vehicle-${vehicle.id}`;
      
      // Stagger rows for visual interest
      const row = Math.floor(idx / 4);
      const col = idx % 4;
      const xOffset = col * VEHICLE_GAP - (Math.min(vehicles.length, 4) - 1) * VEHICLE_GAP / 2;
      
      newNodes.push({
        id: vehicleId,
        type: 'vehicle',
        position: { 
          x: CENTER_X + xOffset,
          y: VEHICLE_Y_START + row * 200,
        },
        data: {
          ...vehicle,
          aiStatus,
        },
      });
      
      // Edge: Gateway → Vehicle
      const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.ATTENTION;
      const edgeColor = hasAlert 
        ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b')
        : vehicle.status === 'offline' ? '#64748b' : '#22d3ee';
      
      newEdges.push({
        id: `gateway-to-${vehicleId}`,
        source: `gateway-${gateway.id}`,
        target: vehicleId,
        type: 'smoothstep',
        animated: vehicle.status !== 'offline',
        style: { stroke: edgeColor, strokeWidth: 2 },
      });
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [vehicles, gateway]);

  // Initialize
  useEffect(() => {
    setLoading(true);
    
    // Simulate initial load
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-500/30 to-blue-600/30 rounded-xl flex items-center justify-center border border-cyan-400/30">
                <span className="text-xl">🚐</span>
              </span>
              Fleet Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">AI-Powered Fleet Management • Real-time Vehicle Monitoring • Predictive Maintenance</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded border border-emerald-500/30">
                Edge AI Platform Demo
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar 
            vehicles={vehicles} 
            anomalies={anomalies} 
            onOpenPanel={() => setIsAIPanelOpen(true)} 
          />
        </div>
      </div>
      
      {/* Proactive Alert */}
      <ProactiveAlert 
        anomalies={anomalies} 
        vehicles={vehicles} 
        onViewDetails={() => setIsAIPanelOpen(true)} 
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-300 text-lg font-medium">AI Sentry Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to Fleet Gateway</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          minZoom={0.3}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.65 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(6, 182, 212, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'fleetCommand') return '#22d3ee';
              if (node.type === 'fleetGateway') return '#34d399';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.ATTENTION) return '#f59e0b';
              if (node.data?.aiStatus === AI_STATUS.OFFLINE) return '#64748b';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        vehicles={vehicles}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default FleetSentry;
